import { Link } from "react-router-dom";
import type { NewsItem } from "@/mocks/news";
import { channelSoftText, getChannel } from "@/utils/channel";
import { formatDate, formatNum } from "@/hooks/useSeo";

export function ChannelLabel({ category }: { category: string }) {
  const c = getChannel(category);
  if (!c) return null;
  return (
    <Link
      to={`/channels?category=${category}`}
      className={`inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium ${channelSoftText(category)}`}
    >
      {c.name}
    </Link>
  );
}

export function FeaturedCard({ item }: { item: NewsItem }) {
  return (
    <article className="group relative overflow-hidden rounded-lg border border-background-200/80 bg-background-50">
      <Link to={`/news/${item.id}`} className="block">
        <div className="relative h-[300px] w-full overflow-hidden md:h-[400px]">
          <img
            src={item.cover}
            alt={item.title}
            title={item.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/20 to-transparent"></div>
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex items-center gap-1 rounded-md bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
                <i className="ri-flashlight-fill text-[10px]"></i>
                今日焦点
              </span>
              <span className="text-xs text-background-50/80">{formatDate(item.date)}</span>
            </div>
            <h3 className="font-display text-xl font-bold leading-snug text-background-50 md:text-2xl">
              {item.title}
            </h3>
            <p className="mt-2 hidden max-w-2xl text-sm leading-6 text-background-50/80 md:block">{item.summary}</p>
            <div className="mt-4 flex items-center gap-4 text-xs text-background-50/70">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {item.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {item.time}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line"></i>
                {formatNum(item.reads)} 阅读
              </span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}

export function StandardCard({ item, showSummary = true }: { item: NewsItem; showSummary?: boolean }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-50">
      <Link to={`/news/${item.id}`} className="block overflow-hidden">
        <div className="relative h-44 w-full overflow-hidden">
          <img
            src={item.cover}
            alt={item.title}
            title={item.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-md bg-foreground-950/70 px-2 py-0.5 text-[11px] text-background-50 backdrop-blur">
            <i className="ri-fire-line text-accent-400"></i>
            {formatNum(item.reads)} 热读
          </span>
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <div className="mb-2 flex items-center justify-between gap-2">
          <ChannelLabel category={item.category} />
          <span className="text-xs text-foreground-400">{formatDate(item.date)}</span>
        </div>
        <Link to={`/news/${item.id}`}>
          <h3 className="font-display text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
            {item.title}
          </h3>
        </Link>
        {showSummary && (
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{item.summary}</p>
        )}
        <div className="mt-auto flex items-center gap-3 pt-3 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-user-line"></i>
            {item.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {item.time}
          </span>
        </div>
      </div>
    </article>
  );
}

export function ListRow({ item, index }: { item: NewsItem; index: number }) {
  return (
    <article className="group flex gap-4 rounded-lg border border-background-200/70 bg-background-50 p-3">
      <Link to={`/news/${item.id}`} className="shrink-0 overflow-hidden rounded-md">
        <img
          src={item.cover}
          alt={item.title}
          title={item.title}
          className="h-20 w-28 object-cover transition-transform duration-500 group-hover:scale-105 md:h-24 md:w-36"
        />
      </Link>
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <div className="flex items-center gap-2">
          <span className="font-display w-5 text-center text-lg font-black text-primary-400">
            {String(index + 1).padStart(2, "0")}
          </span>
          <ChannelLabel category={item.category} />
        </div>
        <Link to={`/news/${item.id}`} className="mt-1.5 line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600 md:text-base">
          {item.title}
        </Link>
        <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-400">
          <span>{formatDate(item.date)}</span>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line"></i>
            {formatNum(item.reads)}
          </span>
        </div>
      </div>
    </article>
  );
}